import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'
import { EmptyState, ErrorState, LoadingState } from '@/design-system/components/QueryState/QueryState'

export interface DataTableColumn<TItem> {
  key: string
  header: ReactNode
  /** Chiffres et montants : alignés à droite, chasse fixe. */
  numeric?: boolean
  /** Nuance portée par une cellule précise — un solde négatif, par exemple. */
  cellClassName?: (item: TItem) => string | undefined
  /** Rend l'en-tête cliquable pour trier; requiert `sort` et `onSortChange` sur la table. */
  sortable?: boolean
  render: (item: TItem) => ReactNode
}

export interface DataTableSort {
  key: string
  direction: 'asc' | 'desc'
}

/** Sélection multiple : colonne de cases à cocher en tête, état piloté par le parent. */
export interface DataTableSelection<TItem> {
  isSelected: (item: TItem) => boolean
  onToggle: (item: TItem) => void
  /** Coche ou décoche toutes les lignes affichées. */
  onToggleAll: () => void
  allSelected: boolean
  someSelected: boolean
}

export interface DataTableProps<TItem> {
  label: string
  columns: DataTableColumn<TItem>[]
  items: TItem[]
  rowKey: (item: TItem) => string
  /** Rend la ligne entière cliquable, comme sur Clients et Contrats. */
  rowHref?: (item: TItem) => string
  rowClassName?: (item: TItem) => string | undefined
  className?: string
  pending?: boolean
  pendingLabel?: string
  error?: boolean
  errorLabel?: string
  /** État vide complet, action comprise. À défaut, un message neutre. */
  empty?: ReactNode
  /**
   * Échappatoire pour une ligne qui porte son propre état — un menu ouvert, un
   * panneau déplié. L'en-tête, les états et la grille restent partagés; seule
   * la composition des cellules revient au module.
   */
  renderRow?: (item: TItem) => ReactNode
  /** Tri courant, piloté par le parent (tri serveur sur une liste paginée). */
  sort?: DataTableSort | undefined
  onSortChange?: (key: string) => void
  selection?: DataTableSelection<TItem>
}

/**
 * Table dense partagée par les listes de modules.
 *
 * Quatre formats de liste coexistaient : chaque module réécrivait son en-tête,
 * ses états de chargement et son état vide autour de la même grille. Les
 * colonnes sont désormais déclarées, et la largeur de chacune reste gouvernée
 * par la feuille de style du module via `className` — la migration ne déplace
 * aucune colonne.
 */
export function DataTable<TItem>({
  label,
  columns,
  items,
  rowKey,
  rowHref,
  rowClassName,
  className,
  pending = false,
  pendingLabel = 'Chargement…',
  error = false,
  errorLabel = 'Cette liste est indisponible.',
  empty,
  renderRow,
  sort,
  onSortChange,
  selection,
}: DataTableProps<TItem>) {
  return (
    <div className={mergeClassNames('master-table', className)} aria-label={label}>
      <div className="master-table__head">
        {selection ? (
          <span>
            <input
              type="checkbox"
              aria-label="Sélectionner tous les éléments affichés"
              checked={selection.allSelected}
              ref={(input) => {
                if (input) input.indeterminate = !selection.allSelected && selection.someSelected
              }}
              onChange={selection.onToggleAll}
            />
          </span>
        ) : null}
        {columns.map((column) => (
          <span
            key={column.key}
            className={column.numeric ? 'master-table__cell--numeric' : undefined}
            aria-sort={
              column.sortable && sort?.key === column.key
                ? sort.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : undefined
            }
          >
            {column.sortable && onSortChange ? (
              <button
                type="button"
                className="master-table__sort"
                onClick={() => {
                  onSortChange(column.key)
                }}
              >
                {column.header}
                <span aria-hidden="true">
                  {sort?.key === column.key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                </span>
              </button>
            ) : (
              column.header
            )}
          </span>
        ))}
      </div>
      {pending ? <LoadingState>{pendingLabel}</LoadingState> : null}
      {error ? <ErrorState>{errorLabel}</ErrorState> : null}
      {!pending && !error && items.length === 0
        ? (empty ?? <EmptyState>Aucun élément à afficher.</EmptyState>)
        : null}
      {renderRow ? items.map((item) => renderRow(item)) : null}
      {renderRow
        ? null
        : items.map((item) => {
            const selectionCell = selection ? (
              <span key="__select">
                <input
                  type="checkbox"
                  aria-label={`Sélectionner ${rowKey(item)}`}
                  checked={selection.isSelected(item)}
                  onClick={(event) => {
                    // La ligne est un lien : cocher ne doit jamais ouvrir la fiche.
                    event.stopPropagation()
                  }}
                  onChange={() => {
                    selection.onToggle(item)
                  }}
                />
              </span>
            ) : null
            const dataCells = columns.map((column) => (
              <span
                key={column.key}
                className={mergeClassNames(
                  column.numeric ? 'master-table__cell--numeric' : undefined,
                  column.cellClassName?.(item),
                )}
              >
                {column.render(item)}
              </span>
            ))
            const cells = selectionCell ? [selectionCell, ...dataCells] : dataCells
            const rowClasses = mergeClassNames('master-table__row', rowClassName?.(item))
            const href = rowHref?.(item)
            return href ? (
              <Link className={rowClasses} key={rowKey(item)} to={href}>
                {cells}
              </Link>
            ) : (
              <div className={rowClasses} key={rowKey(item)}>
                {cells}
              </div>
            )
          })}
    </div>
  )
}
